import React from 'react';
import { SimulationConfig } from './types';
import { SectionLabel } from './SectionLabel';
import { ControlGroup } from './ControlGroup';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { SETTING_INFO } from './settingInfo';

interface GeneralDisplaySectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const GeneralDisplaySection: React.FC<GeneralDisplaySectionProps> = ({ config, setConfig }) => (
  <>
    <SectionLabel icon="fa-display" darkMode={config.darkMode}>Master Display Controls</SectionLabel>
    <div className="flex flex-col gap-1 w-full p-4 rounded-3xl border bg-neutral-500/5 border-neutral-500/10 mb-2">
      <ControlGroup label="Dark Mode" settingKey="darkMode" darkMode={config.darkMode}>
        <Toggle active={config.darkMode} onClick={() => setConfig(p => ({...p, darkMode: !p.darkMode}))} color="bg-neutral-700" />
      </ControlGroup>
      
      <ControlGroup label="RGB Colors" settingKey="rgbLighting" darkMode={config.darkMode}>
        <Toggle active={config.rgbLighting} onClick={() => setConfig(p => ({...p, rgbLighting: !p.rgbLighting}))} color="bg-indigo-500" />
      </ControlGroup>

      <ControlGroup label="Follow Ball" settingKey="cameraFollow" darkMode={config.darkMode}>
        <Toggle active={config.cameraFollow} onClick={() => setConfig(p => ({...p, cameraFollow: !p.cameraFollow}))} />
      </ControlGroup>
    </div>

    <SectionLabel icon="fa-arrows-up-down-left-right" darkMode={config.darkMode}>Ball Size Counter</SectionLabel>
    <div className="flex flex-col gap-1 w-full p-4 rounded-3xl border bg-neutral-500/5 border-neutral-500/10 mb-2">
      <ControlGroup label="Show Counter" settingKey="showBallSizeCounter" darkMode={config.darkMode}>
        <Toggle active={config.showBallSizeCounter} onClick={() => setConfig(p => ({...p, showBallSizeCounter: !p.showBallSizeCounter}))} color="bg-blue-500" />
      </ControlGroup>
      
      {config.showBallSizeCounter && (
        <>
          <ControlGroup label="Position" settingKey="ballSizeCounterPosition">
            <select 
              value={config.ballSizeCounterPosition} 
              onChange={e => setConfig(p => ({...p, ballSizeCounterPosition: e.target.value as any}))}
              className={`w-full px-3 py-1.5 border rounded-xl text-xs font-bold outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
            >
              <option value="TOP_CENTER">Top Center</option>
              <option value="BOTTOM_CENTER">Bottom Center</option>
              <option value="CENTER">Center</option>
              <option value="FOLLOW_BALL">Follow Ball</option>
              <option value="NONE">None</option>
            </select>
          </ControlGroup>
          
          <SmartInput label="Offset" min={-500} max={500} value={config.ballSizeCounterOffset} onChange={v => setConfig(p => ({...p, ballSizeCounterOffset: v}))} darkMode={config.darkMode} settingKey="ballSizeCounterOffset" />
          
          <ControlGroup label="Prefix" settingKey="ballSizeCounterPrefix">
            <input 
              type="text" 
              value={config.ballSizeCounterPrefix} 
              onChange={e => setConfig(p => ({...p, ballSizeCounterPrefix: e.target.value}))} 
              className={`w-full px-3 py-1.5 border rounded-xl text-xs font-bold outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
            />
          </ControlGroup>
          
          <ControlGroup label="Suffix" settingKey="ballSizeCounterSuffix">
            <input 
              type="text" 
              value={config.ballSizeCounterSuffix} 
              onChange={e => setConfig(p => ({...p, ballSizeCounterSuffix: e.target.value}))} 
              className={`w-full px-3 py-1.5 border rounded-xl text-xs font-bold outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
            />
          </ControlGroup>

          <div className="grid grid-cols-2 gap-3">
            <ControlGroup label="Color" settingKey="ballSizeCounterColor">
              <input type="color" value={config.ballSizeCounterColor} onChange={e => setConfig(p => ({...p, ballSizeCounterColor: e.target.value}))} className="w-full h-8 rounded-lg cursor-pointer bg-transparent" />
            </ControlGroup>
            <ControlGroup label="Use RGB" settingKey="ballSizeCounterUseRGB" darkMode={config.darkMode}>
              <Toggle active={config.ballSizeCounterUseRGB} onClick={() => setConfig(p => ({...p, ballSizeCounterUseRGB: !p.ballSizeCounterUseRGB}))} color="bg-indigo-500" />
            </ControlGroup>
          </div>

          <SmartInput label="Font Size" min={10} max={200} value={config.ballSizeCounterFontSize} onChange={v => setConfig(p => ({...p, ballSizeCounterFontSize: v}))} darkMode={config.darkMode} settingKey="ballSizeCounterFontSize" />
          <SmartInput label="Decimals" min={0} max={3} value={config.ballSizeCounterDecimalPlaces} onChange={v => setConfig(p => ({...p, ballSizeCounterDecimalPlaces: v}))} darkMode={config.darkMode} settingKey="ballSizeCounterDecimalPlaces" />
          <SmartInput label="Opacity" min={0} max={1} step={0.1} value={config.ballSizeCounterOpacity} onChange={v => setConfig(p => ({...p, ballSizeCounterOpacity: v}))} darkMode={config.darkMode} settingKey="ballSizeCounterOpacity" />
          
          <div className="grid grid-cols-2 gap-3">
            <ControlGroup label="Shadow Color" settingKey="ballSizeCounterShadowColor">
              <input type="color" value={config.ballSizeCounterShadowColor} onChange={e => setConfig(p => ({...p, ballSizeCounterShadowColor: e.target.value}))} className="w-full h-8 rounded-lg cursor-pointer bg-transparent" />
            </ControlGroup>
            <SmartInput label="Shadow Blur" min={0} max={50} value={config.ballSizeCounterShadowBlur} onChange={v => setConfig(p => ({...p, ballSizeCounterShadowBlur: v}))} darkMode={config.darkMode} settingKey="ballSizeCounterShadowBlur" />
          </div>
        </>
      )}
    </div>

    <SectionLabel icon="fa-basketball" darkMode={config.darkMode}>Bounce Counter</SectionLabel>
    <div className="flex flex-col gap-1 w-full p-4 rounded-3xl border bg-neutral-500/5 border-neutral-500/10 mb-2">
      <ControlGroup label="Show Counter" settingKey="showBounceCounter" darkMode={config.darkMode}>
        <Toggle active={config.showBounceCounter} onClick={() => setConfig(p => ({...p, showBounceCounter: !p.showBounceCounter}))} color="bg-orange-500" />
      </ControlGroup>
      
      {config.showBounceCounter && (
        <>
          <ControlGroup label="Position" settingKey="bounceCounterPosition">
            <select 
              value={config.bounceCounterPosition} 
              onChange={e => setConfig(p => ({...p, bounceCounterPosition: e.target.value as any}))}
              className={`w-full px-3 py-1.5 border rounded-xl text-xs font-bold outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
            >
              <option value="TOP_CENTER">Top Center</option>
              <option value="BOTTOM_CENTER">Bottom Center</option>
              <option value="CENTER">Center</option>
              <option value="FOLLOW_BALL">Follow Ball</option>
              <option value="NONE">None</option>
            </select>
          </ControlGroup>
          
          <SmartInput label="Offset" min={-500} max={500} value={config.bounceCounterOffset} onChange={v => setConfig(p => ({...p, bounceCounterOffset: v}))} darkMode={config.darkMode} settingKey="bounceCounterOffset" />
          
          <ControlGroup label="Prefix" settingKey="bounceCounterPrefix">
            <input 
              type="text" 
              value={config.bounceCounterPrefix} 
              onChange={e => setConfig(p => ({...p, bounceCounterPrefix: e.target.value}))} 
              className={`w-full px-3 py-1.5 border rounded-xl text-xs font-bold outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
            />
          </ControlGroup>
          
          <ControlGroup label="Suffix" settingKey="bounceCounterSuffix">
            <input 
              type="text" 
              value={config.bounceCounterSuffix} 
              onChange={e => setConfig(p => ({...p, bounceCounterSuffix: e.target.value}))} 
              className={`w-full px-3 py-1.5 border rounded-xl text-xs font-bold outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
            />
          </ControlGroup>

          <div className="grid grid-cols-2 gap-3">
            <ControlGroup label="Color" settingKey="bounceCounterColor">
              <input type="color" value={config.bounceCounterColor} onChange={e => setConfig(p => ({...p, bounceCounterColor: e.target.value}))} className="w-full h-8 rounded-lg cursor-pointer bg-transparent" />
            </ControlGroup>
            <ControlGroup label="Use RGB" settingKey="bounceCounterUseRGB" darkMode={config.darkMode}>
              <Toggle active={config.bounceCounterUseRGB} onClick={() => setConfig(p => ({...p, bounceCounterUseRGB: !p.bounceCounterUseRGB}))} color="bg-indigo-500" />
            </ControlGroup>
          </div>

          <SmartInput label="Font Size" min={10} max={200} value={config.bounceCounterFontSize} onChange={v => setConfig(p => ({...p, bounceCounterFontSize: v}))} darkMode={config.darkMode} settingKey="bounceCounterFontSize" />
          <SmartInput label="Opacity" min={0} max={1} step={0.1} value={config.bounceCounterOpacity} onChange={v => setConfig(p => ({...p, bounceCounterOpacity: v}))} darkMode={config.darkMode} settingKey="bounceCounterOpacity" />
          
          <div className="grid grid-cols-2 gap-3">
            <ControlGroup label="Shadow Color" settingKey="bounceCounterShadowColor">
              <input type="color" value={config.bounceCounterShadowColor} onChange={e => setConfig(p => ({...p, bounceCounterShadowColor: e.target.value}))} className="w-full h-8 rounded-lg cursor-pointer bg-transparent" />
            </ControlGroup>
            <SmartInput label="Shadow Blur" min={0} max={50} value={config.bounceCounterShadowBlur} onChange={v => setConfig(p => ({...p, bounceCounterShadowBlur: v}))} darkMode={config.darkMode} settingKey="bounceCounterShadowBlur" />
          </div>
        </>
      )}
    </div>
  </>
);